import { takeLatest, put, call, all } from 'redux-saga/effects';
import get from 'lodash/get';
import {
  addAndUpdateContactsAction,
  getCompanyEditSeasonAction,
  getCompanyUnpublishedSeasonAction,
  updateCompanyInfoAction,
  updateCompanyVenueAction,
  updateCompanyTranslationsAction,
  getCompanyTranslationsAction,
  getCompanyReviewsAction,
  getCompanyProductionReviewsAction,
} from './actions';
// eslint-disable-next-line import/named
import { getProductions, create, updateOne, deleteOne } from '../../utils/API';
import { enqueueSnackbarAction } from '../App/actions';
import { COMPANIES, COMPANY_CONTACTS, VENUES } from '../../constants/endpointConstants';
import { PRODUCTION_ADDED_IDENTIFIERS_IDS, PRODUCTION_VALIDITY_IDENTIFIERS, TP } from '../../constants/consts';
import { i18n } from '../../src/i18n';
import { getWithDefault } from '../../utils/common';
import * as API from '../../utils/API';

export function* fetchCompanyEditSeason({ payload, serverCookies }) {
  try {
    if (payload.companyId) {
      const productionsInSeason = yield call(
        getProductions,
        {
          sort: payload.sort || '-year',
          ...(payload?.query || {}),
          ...(payload.seasonId ? { season_id: payload.seasonId } : {}),
          company_id: payload.companyId,
          approved_by: [
            PRODUCTION_VALIDITY_IDENTIFIERS.COMPANY,
            PRODUCTION_VALIDITY_IDENTIFIERS.ADMIN,
            PRODUCTION_VALIDITY_IDENTIFIERS.FESTIVAL,
          ],
          as_edit: true,
        },
        payload?.pagination || {},
        serverCookies,
      );
      yield put(getCompanyEditSeasonAction.success(get(productionsInSeason, 'data')));
    } else {
      yield put(getCompanyEditSeasonAction.success({ data: [] }));
    }
  } catch (err) {
    yield put(getCompanyEditSeasonAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: i18n.t(`${TP}.FN_ERROR_FETCHING_DATA`, { error: err?.message }),
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getCompanyEditSeasonAction.fulfill());
  }
}

export function* fetchCompanyUnpublishedProductions({ payload, serverCookies }) {
  try {
    const productionsInSeason = yield call(
      getProductions,
      {
        company_id: payload.companyId,
        published: false,
        sort: '-updated_at',
        as_edit: true,
        added_by: PRODUCTION_VALIDITY_IDENTIFIERS.COMPANY, // remove after api is ready for ownership
        for_entity: PRODUCTION_ADDED_IDENTIFIERS_IDS.COMPANY,
        for_entity_id: payload.companyId,
        ...(payload?.query || {}),
      },
      payload.pagination || {},
      serverCookies,
    );
    yield put(getCompanyUnpublishedSeasonAction.success(get(productionsInSeason, 'data')));
  } catch (err) {
    yield put(getCompanyUnpublishedSeasonAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: i18n.t(`${TP}.FN_ERROR_FETCHING_DATA`, { error: err?.message }),
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getCompanyUnpublishedSeasonAction.fulfill());
  }
}

export function* addAndUpdateContacts({ payload, serverCookies }) {
  const callback = get(payload, 'callback');
  const endpoint = COMPANY_CONTACTS(payload?.companyId);

  try {
    const newContacts = getWithDefault(payload, 'newContacts', []);
    const updatingContacts = getWithDefault(payload, 'updatingContacts', []);
    const deletedContactsIds = getWithDefault(payload, 'deletedContactsIds', []);
    yield all([
      ...(newContacts?.length > 0 ? [call(create, endpoint, { data: newContacts }, null, serverCookies)] : []),
      ...(updatingContacts?.length > 0
        ? [call(updateOne, endpoint, updatingContacts[0]?.id, { data: updatingContacts }, null, serverCookies)]
        : []),
      ...deletedContactsIds.map(deletedContactId => call(deleteOne, endpoint, deletedContactId, {}, serverCookies)),
    ]);

    if (callback && typeof callback === 'function') payload.callback(true);
    yield put(addAndUpdateContactsAction.success());
    yield put(
      enqueueSnackbarAction({
        message: i18n.t(`${TP}.FN_SUCCESS_UPDATING_CONTACTS`),
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    if (callback && typeof callback === 'function') payload.callback(false);
    yield put(addAndUpdateContactsAction.failure());
    yield put(
      enqueueSnackbarAction({
        message: i18n.t(`${TP}.FN_ERROR_UPDATING_CONTACTS`, {
          endpoint,
          error: err?.responseText ?? err?.message,
        }),
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export function* updateCompanyVenue({ payload, serverCookies }) {
  const callback = get(payload, 'callback');
  const venues = get(payload, 'venues', []);
  try {
    yield all(venues?.map(v => call(updateOne, VENUES, v?.id, v, null, serverCookies)));
    if (callback && typeof callback === 'function') {
      payload.callback();
    }
    yield put(
      enqueueSnackbarAction({
        message: i18n.t(`${TP}.FN_SUCCESS_UPDATING_VENUE`),
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: i18n.t(`${TP}.FN_ERROR_UPDATING_VENUE`, { error: err?.message }),
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export function* updateCompanyInfo({ payload, serverCookies }) {
  const callback = get(payload, 'callback');
  const companyId = get(payload, 'companyId');
  try {
    yield all([
      call(updateOne, COMPANIES, companyId, payload?.companyInfo, null, serverCookies),
      call(addAndUpdateContacts, {
        payload: {
          companyId,
          newContacts: payload?.newContacts || [],
          updatingContacts: payload?.updatedContacts || [],
          deletedContactsIds: payload?.deletedContacts || [],
        },
      }),
    ]);

    if (callback && typeof callback === 'function') payload.callback(true);
  } catch (err) {
    if (callback && typeof callback === 'function') payload.callback(false);
    yield put(
      enqueueSnackbarAction({
        message: i18n.t(`${TP}.FN_ERROR_UPDATING_COMPANY_INFO`, { error: err?.message }),
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export function* updateCompanyTranslations({ payload, serverCookies }) {
  const callback = get(payload, 'callback');
  const companyId = get(payload, 'companyId');
  try {
    const translations = yield call(
      API.updateCompanyTranslations,
      companyId,
      payload.companyTranslations,
      serverCookies,
    );
    yield put(getCompanyTranslationsAction.success(get(translations, 'data')));

    if (callback && typeof callback === 'function') payload.callback(true);
  } catch (err) {
    if (callback && typeof callback === 'function') payload.callback(false);
    yield put(
      enqueueSnackbarAction({
        message: `Error updating company name: ${err?.message}`,
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export function* fetchCompanyReviews({ payload, serverCookies }) {
  const { url, pagination, query } = payload;
  try {
    const response = yield call(API.getReviewsV1, url, pagination, query ?? {}, serverCookies);
    yield put(getCompanyReviewsAction.success(get(response, 'data')));
  } catch (err) {
    yield put(getCompanyReviewsAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: err?.message,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getCompanyReviewsAction.fulfill());
  }
}

export function* fetchCompanyProductionReviews({ payload, serverCookies }) {
  const { url, pagination, query } = payload;
  try {
    const response = yield call(API.getReviewsV1, url, pagination, query ?? {}, serverCookies);
    yield put(getCompanyProductionReviewsAction.success(get(response, 'data')));
  } catch (err) {
    yield put(getCompanyProductionReviewsAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: err?.message,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getCompanyProductionReviewsAction.fulfill());
  }
}

export default function* companySaga() {
  // TODO redo when published/unpublished productions will be totally ready
  yield takeLatest(getCompanyEditSeasonAction.TRIGGER, fetchCompanyEditSeason);
  yield takeLatest(getCompanyUnpublishedSeasonAction.TRIGGER, fetchCompanyUnpublishedProductions);
  yield takeLatest(addAndUpdateContactsAction.TRIGGER, addAndUpdateContacts);
  yield takeLatest(updateCompanyInfoAction.TRIGGER, updateCompanyInfo);
  yield takeLatest(updateCompanyVenueAction.TRIGGER, updateCompanyVenue);
  yield takeLatest(updateCompanyTranslationsAction.TRIGGER, updateCompanyTranslations);
  yield takeLatest(getCompanyReviewsAction.TRIGGER, fetchCompanyReviews);
  yield takeLatest(getCompanyProductionReviewsAction.TRIGGER, fetchCompanyProductionReviews);
}
